import { createStyles, Group, Loader, Stack } from '@mantine/core';
import { type ReactNode, useEffect, useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import { Button } from '~/domains/design-system/Button';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import ConfirmModalSkeleton from './ConfirmModalSkeleton';

const useStyles = createStyles((theme) => ({
  container: {
    width: 380,

    [theme.fn.smallerThan('sm')]: {
      width: '100%',
    },
  },
}));

type ConfirmModalV2Props = {
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  isLoading?: boolean;
  isOnConfirmLoading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  focusCancel?: boolean;
  children?: ReactNode;
};

function ConfirmModalV2({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  title,
  description,
  isLoading = false,
  isOnConfirmLoading = false,
  confirmLabel,
  cancelLabel,
  focusCancel = false,
  children,
}: ConfirmModalV2Props) {
  const { t } = useTranslation('common');
  const { classes, theme } = useStyles();
  const isMobile = useIsMobile();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen && focusCancel) cancelRef.current?.focus();
  }, [isOpen, focusCancel, isLoading]);

  return (
    <Dialog
      onClose={onClose}
      isOpen={isOpen}
      type={{
        desktop: 'modal',
        mobile: 'drawer',
      }}
      className={classes.container}
    >
      <DialogBody>
        {isLoading ? (
          <ConfirmModalSkeleton />
        ) : (
          <Stack spacing={theme.other.spacing._8} pt={isMobile ? theme.other.spacing._16 : 0}>
            {title && (
              <Title tag="h3" variant="headline-large">
                {title}
              </Title>
            )}
            {description && <Text variant="body-large">{description}</Text>}
            {children}
            <Group spacing={theme.other.spacing._8} pt={theme.other.spacing._16} position="right">
              {onConfirm && (
                <Button
                  variant="primary-fill"
                  size="medium"
                  type="button"
                  onClick={() => {
                    onConfirm?.();
                  }}
                  data-test-id={dt.common.components.modal.confirmModal.confirmButton}
                >
                  {isOnConfirmLoading && (
                    <Loader stroke={theme.white} size="sm" style={{ verticalAlign: 'bottom' }} />
                  )}
                  {!isOnConfirmLoading && (confirmLabel || t('modal.confirm'))}
                </Button>
              )}
              <Button
                ref={cancelRef}
                variant="secondary-stroke"
                size="medium"
                onClick={() => {
                  onCancel?.();
                  onClose();
                }}
                data-test-id={dt.common.components.modal.confirmModal.cancelButton}
              >
                {cancelLabel || t('modal.cancel')}
              </Button>
            </Group>
          </Stack>
        )}
      </DialogBody>
    </Dialog>
  );
}

export default ConfirmModalV2;
